/**
 * Groups management for one organization (Feature 29).
 *
 * Groups are referenced by id from ingest-declared ACLs (`allowed_groups`
 * on vector-store payloads); the platform only manages membership. The
 * member picker offers the org's accounts and adds any number of them in a
 * single call, reporting per-user failures rather than aborting the batch.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cloneElement, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@optum/api-client/client';
import { listAccounts } from '@optum/api-client/endpoints/accounts';
import {
  addGroupMembers,
  createGroup,
  deleteGroup,
  listGroupMembers,
  listGroupCollections,
  listGroups,
  removeGroupMember,
} from '@optum/api-client/endpoints/groups';
import type { Group } from '@optum/api-client/types';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { useConfirm } from '../../ui/Confirm.js';
import { Field } from '../../ui/Field.js';
import { Avatar, Badge, Icon, IconButton, Input, Typography } from '../../ui/finixt.js';

export function GroupsTab({ organizationId }: { organizationId: string }) {
  const query = useQuery({
    queryKey: qk.groups(organizationId),
    queryFn: () => listGroups(organizationId),
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <CreateGroupForm organizationId={organizationId} />
      {query.data.length === 0 ? (
        <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          No groups yet. Groups are granted access to ingested data via
          <code style={{ marginLeft: 5, marginRight: 5 }}>allowed_groups</code> at ingestion time.
        </p>
      ) : (
        query.data.map((group) => (
          <GroupCard
            key={group.id}
            organizationId={organizationId}
            group={group}
            expanded={expanded === group.id}
            onToggle={() => setExpanded(expanded === group.id ? null : group.id)}
          />
        ))
      )}
    </div>
  );
}

/**
 * One group, leading with the two numbers that decide whether it grants
 * anything: who may ask, and what they may reach.
 *
 * Three states, not two. `unreachable` is the one that costs something — the
 * collections are granted only through a group nobody belongs to, so the
 * documents behind them can be retrieved by no one. `grants nothing` is the
 * inverse and is merely useless. Neither is visible on a member list with a
 * groups column, which is why this page is group-first.
 *
 * A group with neither members nor collections reads as `grants nothing`
 * rather than `unreachable`: nothing is stranded, so the red state would be a
 * standing false alarm.
 */
type GroupState = {
  label: 'healthy' | 'grants nothing' | 'unreachable';
  tone: 'ok' | 'warn' | 'danger';
};

export function groupState(members: number, collections: number): GroupState {
  if (collections > 0 && members === 0) return { label: 'unreachable', tone: 'danger' };
  if (collections === 0) return { label: 'grants nothing', tone: 'warn' };
  return { label: 'healthy', tone: 'ok' };
}

function GroupCard({
  organizationId,
  group,
  expanded,
  onToggle,
}: {
  organizationId: string;
  group: Group;
  expanded: boolean;
  onToggle: () => void;
}) {
  const panel = useRecordPanel();
  const reach = useQuery({
    queryKey: qk.groupCollections(organizationId, group.id),
    queryFn: () => listGroupCollections(organizationId, group.id),
  });
  const collections = reach.data ?? [];
  const stranded = collections.reduce((sum, c) => sum + c.doc_count, 0);
  const empty = group.member_count === 0;
  // Until the reach is known, no state is claimed: a card that flashes
  // "unreachable" and then settles on healthy trains an operator to ignore it.
  const state = reach.isSuccess ? groupState(group.member_count, collections.length) : null;
  return (
    <div
      style={{
        overflow: 'hidden',
        borderRadius: 'var(--radius-card)',
        border: `1px solid ${state?.tone === 'danger' ? 'var(--danger)' : state?.tone === 'warn' ? 'var(--warn)' : 'var(--border-hairline)'}`,
        background: 'var(--surface-card)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          borderBottom: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 11,
          paddingBottom: 11,
        }}
      >
        <span
          style={{
            display: 'grid',
            width: 32,
            height: 32,
            flexShrink: 0,
            placeItems: 'center',
            borderRadius: 'var(--radius-input)',
            background:
              state?.tone === 'danger'
                ? 'var(--danger-tint)'
                : state?.tone === 'warn'
                  ? 'var(--warn-tint)'
                  : 'var(--violet-tint)',
            color:
              state?.tone === 'danger'
                ? 'var(--danger)'
                : state?.tone === 'warn'
                  ? 'var(--warn)'
                  : 'var(--violet)',
          }}
        >
          <Icon name="shield_person" size={16} />
        </span>
        <button
          type="button"
          onClick={() => panel.open({ kind: 'group', id: group.id, orgId: organizationId })}
          aria-pressed={panel.current?.kind === 'group' && panel.current.id === group.id}
          style={{ minWidth: 0, flex: '1 1 0%', textAlign: 'left' }}
        >
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-body-md)',
              fontWeight: 'var(--weight-bold)',
              color: 'var(--text-strong)',
            }}
            className="mono"
          >
            {group.name}
          </span>
          {group.description && (
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--text-meta-lg)',
                color: 'var(--text-muted)',
              }}
            >
              {group.description}
            </span>
          )}
        </button>
        {state && <Badge tone={state.tone}>{state.label}</Badge>}
        <GroupDeleteButton organizationId={organizationId} group={group} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <div
          style={{
            borderColor: 'var(--border-hairline)',
            padding: 16,
            borderRight: '1px solid var(--border-hairline)',
          }}
        >
          <div style={{ marginBottom: 11, display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <Typography variant="eyebrow">Members</Typography>
            <span
              style={{
                fontSize: 'var(--text-title)',
                lineHeight: 1,
                fontWeight: 'var(--weight-bold)',
                color: 'var(--text-strong)',
              }}
              className="mono"
            >
              {group.member_count}
            </span>
            <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
              who may ask
            </span>
            <button
              type="button"
              onClick={onToggle}
              style={{
                marginLeft: 'auto',
                fontSize: 'var(--text-meta-lg)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--text-accent)',
              }}
              className="mono"
            >
              {expanded ? 'Hide' : 'Manage'}
            </button>
          </div>
          {/* Red only when the emptiness strands something. A group with no
              members and no collections is idle, not broken. */}
          {empty ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                borderRadius: 'var(--radius-input)',
                border: `1px dashed ${state?.tone === 'danger' ? 'var(--danger)' : 'var(--border-hairline)'}`,
                padding: '7px 11px',
              }}
            >
              <Icon
                name="warning"
                size={14}
                color={state?.tone === 'danger' ? 'var(--danger)' : 'var(--text-muted)'}
                style={{ flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: 'var(--text-meta-lg)',
                  fontWeight: 'var(--weight-semibold)',
                  color: state?.tone === 'danger' ? 'var(--danger)' : 'var(--text-muted)',
                }}
              >
                Nobody is in this group
              </span>
            </div>
          ) : (
            <MemberStack
              organizationId={organizationId}
              groupId={group.id}
              total={group.member_count}
            />
          )}
        </div>

        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 11, display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <Typography variant="eyebrow">Collections</Typography>
            <span
              className="mono"
              style={{
                fontSize: 'var(--text-title)',
                lineHeight: 1,
                fontWeight: 'var(--weight-bold)',
                color: reach.isSuccess ? 'var(--text-strong)' : 'var(--text-faint)',
              }}
            >
              {reach.isSuccess ? collections.length : '—'}
            </span>
            <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
              what they may reach
            </span>
          </div>
          {reach.isError ? (
            <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
              Could not load reach.
            </span>
          ) : collections.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--warn)',
                borderStyle: 'dashed',
                paddingLeft: 11,
                paddingRight: 11,
                paddingTop: 7,
                paddingBottom: 7,
              }}
            >
              <Icon name="warning" size={14} style={{ flexShrink: 0, color: 'var(--warn)' }} />
              <span
                style={{
                  fontSize: 'var(--text-meta-lg)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--warn)',
                }}
              >
                Grants access to nothing
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {collections.map((c) => (
                <div
                  key={c.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    borderRadius: 'var(--radius-control)',
                    background: 'var(--surface-fill)',
                    paddingLeft: 7,
                    paddingRight: 7,
                    paddingTop: 5,
                    paddingBottom: 5,
                  }}
                >
                  <span
                    style={{
                      flex: '1 1 0%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 'var(--text-meta-lg)',
                      color: 'var(--text-body)',
                    }}
                    className="mono"
                  >
                    {c.path}
                  </span>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 'var(--text-meta-lg)',
                      color: 'var(--text-muted)',
                    }}
                    className="mono"
                  >
                    {c.doc_count} docs
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {state?.tone === 'danger' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 11,
            borderTop: '1px solid var(--border-hairline)',
            borderColor: 'var(--border-hairline)',
            background: 'var(--danger-tint)',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 11,
            paddingBottom: 11,
          }}
        >
          <Icon
            name="info"
            size={15}
            style={{ marginTop: 1, flexShrink: 0, color: 'var(--danger)' }}
          />
          <span
            style={{ fontSize: 'var(--text-meta-lg)', lineHeight: 1.625, color: 'var(--danger)' }}
          >
            This group has no members but {stranded} document{stranded === 1 ? '' : 's'} grant
            access only to it — nobody can retrieve them.
          </span>
        </div>
      )}

      {expanded && (
        <div
          style={{
            borderTop: '1px solid var(--border-hairline)',
            borderColor: 'var(--border-hairline)',
            background: 'var(--surface-sunken)',
            padding: 16,
          }}
        >
          <GroupMembers organizationId={organizationId} groupId={group.id} />
        </div>
      )}
    </div>
  );
}

function CreateGroupForm({ organizationId }: { organizationId: string }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const qc = useQueryClient();
  const toast = useToast();

  const create = useMutation({
    mutationFn: () =>
      createGroup(organizationId, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Group created');
      setName('');
      setDescription('');
      qc.invalidateQueries({ queryKey: qk.groups(organizationId) });
    },
    onError: (err) =>
      toast.error('Create failed', err instanceof ApiError ? err.message : String(err)),
  });

  function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (name.trim()) create.mutate();
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', alignItems: 'flex-end', gap: 11 }}>
      <div style={{ flex: '1 1 0%' }}>
        <Field id="group-name" label="New group">
          <Input
            id="group-name"
            value={name}
            placeholder="e.g. sales-team"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
      </div>
      <div style={{ flex: '1 1 0%' }}>
        <Field id="group-desc" label="Description">
          <Input
            id="group-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
      <Button variant="primary" type="submit" disabled={create.isPending || !name.trim()}>
        {create.isPending ? 'Creating…' : 'Create'}
      </Button>
    </form>
  );
}

/**
 * The faces behind the count.
 *
 * One request per group, which is why it only runs for groups that have
 * members — the empty case is the failure state and renders its own callout
 * instead. Groups per tenant are few; if that stops being true this wants a
 * members-included group list rather than a fan-out.
 */
function MemberStack({
  organizationId,
  groupId,
  total,
}: {
  organizationId: string;
  groupId: string;
  total: number;
}) {
  const members = useQuery({
    queryKey: qk.groupMembers(organizationId, groupId),
    queryFn: () => listGroupMembers(organizationId, groupId),
    staleTime: 60_000,
  });
  const shown = (members.data ?? []).slice(0, 6);
  const more = total - shown.length;

  if (members.isPending) {
    return (
      <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        Loading members…
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
      {shown.map((m) => (
        <Avatar initials={(m.username ?? m.email).slice(0, 2).toUpperCase()} size={32} />
      ))}
      {more > 0 && (
        <span
          style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
          className="mono"
        >
          +{more}
        </span>
      )}
    </div>
  );
}

function GroupDeleteButton({ organizationId, group }: { organizationId: string; group: Group }) {
  const qc = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: () => deleteGroup(organizationId, group.id),
    onSuccess: () => {
      toast.success('Group deleted');
      qc.invalidateQueries({ queryKey: qk.groups(organizationId) });
    },
    onError: (err) =>
      toast.error('Delete failed', err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <ConfirmAction
      trigger={
        <IconButton
          size={28}
          icon="delete"
          aria-label={`Delete ${group.name}`}
          disabled={remove.isPending}
          tone="bare"
        />
      }
      title="Delete this group?"
      description={
        <>
          <strong>{group.name}</strong> will be removed. Documents that list this group in{' '}
          <code>allowed_groups</code> keep the stale id, which simply stops matching.
        </>
      }
      confirmLabel="Delete"
      onConfirm={() => remove.mutate()}
    />
  );
}

function GroupMembers({ organizationId, groupId }: { organizationId: string; groupId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const members = useQuery({
    queryKey: qk.groupMembers(organizationId, groupId),
    queryFn: () => listGroupMembers(organizationId, groupId),
  });
  const accounts = useQuery({
    queryKey: qk.accounts(),
    queryFn: listAccounts,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: qk.groupMembers(organizationId, groupId) });
    qc.invalidateQueries({ queryKey: qk.groups(organizationId) });
  };

  const add = useMutation({
    mutationFn: (platformUserIds: string[]) =>
      addGroupMembers(organizationId, groupId, platformUserIds),
    onSuccess: (result) => {
      // Partial success is the normal case for a batch built from a list that
      // may have gone stale, so report both halves rather than just the count.
      if (result.failed.length === 0) {
        toast.success(`Added ${result.added.length}`);
      } else {
        toast.error(
          `Added ${result.added.length}, ${result.failed.length} failed`,
          result.failed.map((f) => f.reason).join(' '),
        );
      }
      setSelectedIds([]);
      invalidate();
    },
    onError: (err) =>
      toast.error('Add failed', err instanceof ApiError ? err.message : String(err)),
  });

  const remove = useMutation({
    mutationFn: (platformUserId: string) =>
      removeGroupMember(organizationId, groupId, platformUserId),
    onSuccess: () => {
      toast.success('Member removed');
      invalidate();
    },
    onError: (err) =>
      toast.error('Remove failed', err instanceof ApiError ? err.message : String(err)),
  });

  if (members.isPending) return <LoadingBlock />;
  if (members.isError) {
    return (
      <ErrorBlock
        message={members.error instanceof Error ? members.error.message : 'Failed to load'}
      />
    );
  }

  const memberEmails = new Set(members.data.map((m) => m.email));
  const candidates = (accounts.data ?? []).filter(
    (a) => a.organizations.includes(organizationId) && !memberEmails.has(a.email),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>Members</div>
      {members.data.length === 0 ? (
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>No members.</p>
      ) : (
        <ul style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {members.data.map((m) => (
            <li
              key={m.platform_user_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 'var(--text-body-md)',
              }}
            >
              <span style={{ flex: '1 1 0%' }}>{m.email}</span>
              {m.username && <span style={{ color: 'var(--text-muted)' }}>{m.username}</span>}
              <Button
                variant="ghost"
                size="sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate(m.platform_user_id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      {candidates.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div
            style={{
              maxHeight: 176,
              overflowY: 'auto',
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--border-input)',
            }}
          >
            {candidates.map((a) => {
              const checked = selectedIds.includes(a.platform_user_id);
              return (
                <label
                  key={a.platform_user_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    paddingLeft: 7,
                    paddingRight: 7,
                    paddingTop: 7,
                    paddingBottom: 7,
                    fontSize: 'var(--text-body-md)',
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() =>
                      setSelectedIds((prev) =>
                        checked
                          ? prev.filter((id) => id !== a.platform_user_id)
                          : [...prev, a.platform_user_id],
                      )
                    }
                  />
                  <span style={{ flex: '1 1 0%' }}>{a.email}</span>
                </label>
              );
            })}
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={selectedIds.length === 0 || add.isPending}
            onClick={() => add.mutate(selectedIds)}
          >
            {add.isPending ? 'Adding…' : `Add${selectedIds.length ? ` ${selectedIds.length}` : ''}`}
          </Button>
        </div>
      )}
    </div>
  );
}

interface ConfirmActionProps {
  trigger: ReactElement<{ onClick?: (() => void) | undefined }>;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
}

function ConfirmAction({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: ConfirmActionProps) {
  const confirm = useConfirm();
  return cloneElement(trigger, {
    onClick: () => {
      void confirm({ title, description, confirmLabel, tone: 'danger' }).then((ok) => {
        if (ok) onConfirm();
      });
    },
  });
}
